import MUITooltip from '@mui/material/Tooltip';
import { DateWrapper, Tooltip } from '@trucktrax/trucktrax-common';
import { UrlKeyDto } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import { orderBy } from 'lodash';
import React, { Component } from 'react';

import ReactList from 'react-list';
import { connect } from 'react-redux';

import { DTO_URL_PREFIXES, MessagesTabs } from '../../../../../constants/appConstants';
import constants, { MessageTabs } from '../../../../../constants/messagingConstants';
import { ReduxState } from '../../../../../store';
import { setOpenMapFilters } from '../../../../../store/actions/mapFilterActions';
import { getTruckAliasLookup } from '../../../../../store/selectors/messageSelectors';
import { getIdFromUrl, isEmptyArray } from '../../../../../util/appUtil';
import recordLog, { LogLevel } from '../../../../../util/errorUtil';
import AutoCompleteWithChip, { DataItem } from '../../AutoCompleteWithChip';
import { ConversationMessagesDto, ConversationToMessagesDto, Driver } from '../../index.d';
import {
  checkAckTime,
  checkBodyForIconString,
  getRecipientNames,
  getRecipientNamesWithTruckInfo,
} from '../../message';
import styles from './MessageList.module.css';

type MessageListProps = MessageListOwnProps &
  MessageListDispatchProps &
  ReturnType<typeof mapStateToProps>;
class MessageList extends Component<MessageListProps, MessageListState> {
  static defaultProps = {
    selectedMessage: null,
    currentRegion: { url: '' },
  };

  nodeRef: ReactList | null = null;

  constructor(props: MessageListProps) {
    super(props);

    this.state = {
      searchTerm: undefined,
      selectedItems: [],
      shouldBlurAutoCompletePopover: true,
    };
  }

  componentDidUpdate(prevProps: MessageListProps) {
    if (prevProps.currentTab !== this.props.currentTab && this.nodeRef) {
      this.nodeRef.scrollTo(0);
    }
  }

  onClick = (message: ConversationMessagesDto) => {
    this.props.onItemClicked(message);
  };

  markUnread = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    message: ConversationMessagesDto,
  ) => {
    evt.stopPropagation();
    this.props.onMarkUnread(message);
  };

  acknowledgementIndicator = (
    latestMessageRead: boolean,
    latestIsFromDispatcher: boolean,
    conversations: ConversationMessagesDto,
  ) => {
    if (!latestMessageRead) {
      return <span className={styles.dot} />;
    }

    return latestIsFromDispatcher ? (
      <span className={styles.acknowledgedDot} />
    ) : (
      <Tooltip text="Mark as Unread">
        <button
          className={styles.acknowledgedDot}
          onClick={e => this.markUnread(e, conversations)}
        />
      </Tooltip>
    );
  };

  getList(messages: ConversationToMessagesDto[]) {
    const { driverList, selectedProductLine } = this.props;
    const groupedMessages = MessageList.groupMessages(messages);

    return groupedMessages.map(conversations => {
      const latestMessageIdx = conversations.items.length - 1;
      const message = conversations.items[latestMessageIdx];
      let partiesListDriverCount = 0;
      if (message.partiesList) {
        partiesListDriverCount = conversations.latestMessage!.partiesList!.filter(obj =>
          obj.url.includes(DTO_URL_PREFIXES.DRIVERS),
        ).length;
      } else {
        partiesListDriverCount = conversations.partiesList.filter(obj =>
          obj.url.includes(DTO_URL_PREFIXES.DRIVERS),
        ).length;
      }
      // Get recipient info with separated truck data for better display
      const recipientInfo = getRecipientNamesWithTruckInfo(conversations, this.props.getTruckAlias);
      const primaryRecipient = recipientInfo?.at(0) ?? null;

      // Fallback to original logic if no recipient info available
      const fallbackAuthor =
        getRecipientNames(conversations, this.props.getTruckAlias) ||
        conversations.commaSeparatedParties.replace(constants.DISPATCH_COMMA, '');

      const authorName = primaryRecipient?.authorName || fallbackAuthor;
      const truckAlias = primaryRecipient?.truckAlias;
      const authorOnly = `${authorName.replace(constants.DISPATCH, '')}`;

      // checking if parties list includes all the drivers plus the dispatcher.
      const isAllDriversInProductLine = conversations.partiesList.length === driverList.length + 1;
      const productLineString =
        selectedProductLine === constants.READY_MIX_VALUE
          ? constants.READY_MIX_STRING
          : selectedProductLine;
      const acks = conversations.conversationAcknowledgements;
      const ackIndex = acks.findIndex(
        ack => ack.partyMember?.url === this.props.currentRegion?.url,
      );
      const ack = acks[ackIndex];

      if (ack !== undefined) {
        const lastMessageIsAcknowledged = !checkAckTime(message.sent, ack.lastAcknowledged);
        const lastMessageIsFromDispatcher = message.from?.url?.includes(constants.USERS) ?? false;
        const latestMessageRead = lastMessageIsAcknowledged || lastMessageIsFromDispatcher;

        let selected = false;

        if (this.props.selectedMessage) {
          const latestMessageIndex = this.props.selectedMessage.items.length - 1;
          const latestMessage = this.props.selectedMessage.items[latestMessageIndex];
          selected = latestMessage?.conversation?.url === message?.conversation?.url;
        }

        return (
          <div
            data-test="message-center-messagelist-item"
            key={message.id}
            role="button"
            tabIndex={0}
            className={cx(styles.messageItem, { [styles.selected]: selected })}
            onClick={() => this.onClick(conversations)}
            onKeyPress={() => this.onClick(conversations)}
          >
            {this.acknowledgementIndicator(
              latestMessageRead,
              lastMessageIsFromDispatcher,
              conversations,
            )}
            {/* header */}
            <div
              className={styles.header}
              style={{
                alignItems: 'flex-start',
              }}
            >
              <div
                className={cx(styles.author, {
                  [styles.unreadText]: !latestMessageRead,
                })}
                style={{
                  flex: 1,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    overflow: 'hidden',
                  }}
                >
                  {partiesListDriverCount > 1 ? (
                    <i aria-hidden="true" className="icon-people icon-lg icon-people-margin" />
                  ) : (
                    <i />
                  )}
                  <div
                    style={{
                      marginLeft: '4px',
                      lineHeight: '1.2',
                      flex: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <MUITooltip
                      title={this.getConversationMembersTooltip(conversations)}
                      placement="top"
                      enterDelay={600}
                      arrow
                      classes={{
                        tooltip: styles.conversationTooltip,
                        popper: styles.conversationTooltipPopper,
                      }}
                    >
                      <div className="nowrap-ellipsis">
                        {isAllDriversInProductLine
                          ? `All ${productLineString} Drivers`
                          : authorOnly}
                      </div>
                    </MUITooltip>
                    {truckAlias && !isAllDriversInProductLine && (
                      <div
                        className="nowrap-ellipsis"
                        style={{
                          fontSize: '0.75em',
                          color: '#666',
                          opacity: 0.8,
                          marginTop: '2px',
                        }}
                      >
                        {truckAlias}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* time stamp */}
              <div className={styles.timeContainer}>
                {MessageList.renderFormattedTime(new Date(message.sent))}
              </div>
            </div>
            {/* preview */}
            <div className={styles.preview}>{checkBodyForIconString(message.body)}</div>
          </div>
        );
      }
      return null;
    });
  }

  itemRenderer = (index: number, messages: ConversationToMessagesDto[]) => {
    const getSortProperty = (message: ConversationToMessagesDto) => {
      const latestMessageIndex = message.conversations.items.length - 1;
      const latestMessage = message.conversations.items[latestMessageIndex];
      return latestMessage.sent;
    };
    // sort inbox by read and sent
    const sortedMessageList = orderBy(messages, [message => getSortProperty(message)], ['desc']);
    const renderedItems = this.getList(sortedMessageList);
    return renderedItems[index];
  };

  static groupMessages = (messages: ConversationToMessagesDto[]) => {
    const result = messages.filter(m => !!m).map(m => m.conversations);

    return result;
  };

  determineMessagesToShow = (): ConversationToMessagesDto[] => {
    const urls = this.state.selectedItems.map(item => item.url);
    if (isEmptyArray(urls)) {
      return this.props.messages;
    }

    const messages: ConversationToMessagesDto[] = [];
    this.props.messages.forEach(message => {
      const memberUrls = message.conversations?.partiesList?.map(member => member.url) ?? [];
      const isMissingAnyMember = urls.some(url => !memberUrls.includes(url));
      if (isMissingAnyMember) {
        return;
      }

      messages.push(message);
    });
    return messages;
  };

  renderMessages = () => {
    const messages = this.determineMessagesToShow();
    const noMessagesToShow = !messages || isEmptyArray(messages);
    if (noMessagesToShow) {
      return this.renderNoMessagesContent();
    }

    return (
      <ReactList
        itemRenderer={index => this.itemRenderer(index, messages) as any}
        length={messages.length}
        type="uniform"
        threshold={30}
        ref={node => {
          this.nodeRef = node;
        }}
      />
    );
  };

  handleOpenMapFilters = (open: boolean) => () => {
    this.props.setOpenMapFilters(open);
  };

  handleOpenComposeMessageTab = () => () =>
    this.props.onTabChange(MessageTabs.New, this.state.selectedItems);

  renderNoMessagesContent = () => {
    const hasAnySelections = this.state.selectedItems.length > 0;
    let noMatchesReasonContent = (
      <span className={styles.emptyMessageText}>
        No messages to show check
        <button className={styles.mapFilterButton} onClick={this.handleOpenMapFilters(true)}>
          Map Filters
        </button>
      </span>
    );
    if (hasAnySelections) {
      noMatchesReasonContent = (
        <span>
          <button
            className={styles.composeMessageButton}
            onClick={this.handleOpenComposeMessageTab()}
          >
            <i className="icon-translate" />
            Compose a New Message
          </button>
        </span>
      );
    }

    return <div className={styles.emptyMessageContainer}>{noMatchesReasonContent}</div>;
  };

  handleOnInputValueChange = (value: string | undefined) => {
    if (value !== undefined && this.state.searchTerm !== value) {
      this.setState({
        searchTerm: value ?? '',
      });
    }
  };

  handleChange = (value: DataItem) => {
    const alreadySelected = this.state.selectedItems.some(item => item.url === value.url);
    if (alreadySelected) {
      this.handleRemoveItem(value);
    } else {
      this.setState({
        selectedItems: [...this.state.selectedItems, value],
      });
    }

    this.setState({
      searchTerm: '',
    });
  };

  handleRemoveItem = (removedItem: DataItem) => {
    const selectedItems = this.state.selectedItems.filter(item => item.url !== removedItem.url);
    this.setState({
      selectedItems,
    });
  };

  clearAllSelectedItems = () => {
    this.setState({
      selectedItems: [],
    });
  };

  getConversationMembersTooltip = (conversations: ConversationMessagesDto) => {
    const members = conversations.partiesList
      .filter(party => party.fullName) // Filter first
      .map(party => {
        // Enhanced name with truck info for drivers
        if (this.props.getTruckAlias && party.url.includes(DTO_URL_PREFIXES.DRIVERS)) {
          const truckAlias = this.props.getTruckAlias(party.url);
          return truckAlias ? `${party.fullName} (${truckAlias})` : party.fullName;
        }
        return party.fullName;
      });

    return (
      <>
        <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>Conversation members:</div>
        {members.map(name => (
          <div key={name} style={{ marginBottom: '3px', whiteSpace: 'nowrap' }}>
            • {name}
          </div>
        ))}
      </>
    );
  };

  static renderFormattedTime = (time: Date) => {
    if (time && DateWrapper.isValidDate(time)) {
      const today = DateWrapper.now.startOfDay;
      const yesterday = today.previousDay;
      const now = new DateWrapper(time);
      const timeString = now.toString('h:mma');

      if (now.isAfterOrEqual(today)) {
        // today show time only
        return <span className={styles.time}>{timeString}</span>;
      }
      if (now.isBetween(yesterday, today) || now.isEqual(yesterday)) {
        // yesterday show date and time stacked
        return (
          <div className={styles.timeStacked}>
            <div className={styles.timeDate}>Yesterday</div>
            <div className={styles.timeTime}>{timeString}</div>
          </div>
        );
      }
      if (now.isBefore(yesterday)) {
        // before yesterday show date and time stacked
        const dateString = now.toString('M/d/yy');
        return (
          <div className={styles.timeStacked}>
            <div className={styles.timeDate}>{dateString}</div>
            <div className={styles.timeTime}>{timeString}</div>
          </div>
        );
      }
    }
    // Log unknown format and return empty fragment for safety
    recordLog(LogLevel.Warn, 'Invalid time format received in renderFormattedTime', { time });
    return <></>;
  };

  renderClearChips = () => {
    const showClearChips = !isEmptyArray(this.state.selectedItems);
    if (!showClearChips) {
      return <></>;
    }

    return (
      <button
        data-test="clear-chips-button"
        className={styles.clearChips}
        onClick={() => this.clearAllSelectedItems()}
      >
        <i aria-hidden="true" className="icon-cancel" />
      </button>
    );
  };

  determineAvailableDrivers = () => {
    const seenUrls = new Set(this.state.selectedItems.map(item => item.url));
    const uniqueItems = new Map<string, DataItem>();

    this.props.messages.forEach(message => {
      message.conversations.partiesList
        .filter(party => party.fullName && !seenUrls.has(party.url))
        .forEach(party => {
          if (!uniqueItems.has(party.url)) {
            // Use truck-enhanced name for drivers in dropdown (compact format)
            let enhancedName = party.fullName;
            if (this.props.getTruckAlias && party.url.includes(DTO_URL_PREFIXES.DRIVERS)) {
              const truckAlias = this.props.getTruckAlias(party.url);
              if (truckAlias) {
                enhancedName = `${party.fullName} (${truckAlias})`;
              }
            }

            uniqueItems.set(party.url, {
              fullName: enhancedName,
              url: party.url,
              id: parseInt(getIdFromUrl(party.url), 10),
            });
          }
        });
    });

    return Array.from(uniqueItems.values());
  };

  renderInboxSearch = () => {
    const items: DataItem[] = this.determineAvailableDrivers();
    const { selectedItems } = this.state;
    const { searchTerm } = this.state;
    const inputValue = searchTerm ?? '';

    return (
      <div className={styles.inboxDriverContainer}>
        <div className={styles.chipInputContainer}>
          <label htmlFor="fromLabel" className={styles.label}>
            FROM:
          </label>
          <AutoCompleteWithChip
            ref={element => {
              if (!element || !element.popoverInputNode) {
                return;
              }

              if (this.state.shouldBlurAutoCompletePopover) {
                this.setState({ shouldBlurAutoCompletePopover: false }, () => {
                  element.popoverInputNode.blur();
                });
              }
            }}
            availableItems={items}
            partiesList={selectedItems}
            selectedItem={selectedItems}
            inputValue={inputValue}
            onInputValueChange={this.handleOnInputValueChange}
            onChange={this.handleChange}
            onRemoveItem={this.handleRemoveItem}
          />
          {this.renderClearChips()}
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className={styles.messageList}>
        {this.renderInboxSearch()}
        {this.renderMessages()}
      </div>
    );
  }
}

interface MessageListState {
  searchTerm: string | undefined;
  selectedItems: any[];
  shouldBlurAutoCompletePopover: boolean;
}

type MessageListDispatchProps = {
  setOpenMapFilters: typeof setOpenMapFilters;
};

interface MessageListOwnProps {
  messages: ConversationToMessagesDto[];
  onItemClicked: (thread: ConversationMessagesDto) => void;
  onMarkUnread: (thread: ConversationMessagesDto) => void;
  selectedMessage?: ConversationMessagesDto | null;
  currentTab: MessageTabs;
  currentRegion: UrlKeyDto;
  driverList: Driver[];
  selectedProductLine?: string;
  onTabChange: (value: MessagesTabs, selectedItems?: DataItem[]) => void;
  getTruckAlias?: (driverUrl?: string) => string | undefined;
}

const mapStateToProps = (state: ReduxState) => ({
  getTruckAlias: getTruckAliasLookup(state),
});

export default connect<
  ReturnType<typeof mapStateToProps>,
  MessageListDispatchProps,
  MessageListOwnProps,
  ReduxState
>(mapStateToProps, { setOpenMapFilters })(MessageList);
